/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { domain, subdomains, root, SMALL_DEVICE_WIDTH, MEDIUM_DEVICE_WIDTH } from "@/common/consts";
import { readCookieString, cookieName } from "@/common/experiments";

/**
 * Get the current Key-Value string
 * @return {String} The value for `page_variation` KV, formed by a string
 * identifying the current template and the experiment
 */
export const getPageVariationKV = () => {
    const loc = window.location;
    const subdomain = loc.host.replace(domain, "");
    const section =
        loc.pathname === "/"
            ? "home"
            : loc.pathname !== "" && !loc.pathname.startsWith("/index")
            ? "detail"
            : loc.pathname.startsWith("/index")
            ? "index"
            : "";

    let cookieData;
    if (window[cookieName]) {
        cookieData = window[cookieName].channel;
    } else {
        var r = new RegExp(`${cookieName}=([^;]*);`);
        var match = document.cookie.match(r);
        if (match !== null) {
            var cookieString = match[1];
            cookieData = readCookieString(cookieString);
            window[cookieName] = cookieData;
        }
    }

    /** This value represents `variation_A` out of `variation_B`,
     * `variation_C`, `variation_D`, etc., and should be
     *  set dynamically when a system for A/B testing is in place.
     */
    const variation = `v_${cookieData}`;

    const keyValue =
        subdomain === "es" && loc.pathname !== "/"
            ? `${subdomain}_sentence_${section}_${variation}`
            : subdomain === "abbreviations" ||
              subdomain === "reference" ||
              subdomain === "esl" ||
              subdomain === "education"
            ? `reference_${section}_${variation}`
            : `${subdomain}_${section}_${variation}`;

    return keyValue;
};

/**
 * Get the current page `page` GPT Key-Value string
 * @return {String} The value for `page` KV, formed by a string
 */

export const getPageNameKV = () => {
    const loc = window.location;
    const section = loc.host.replace(domain, "");
    const keyValue = `${section}`;
    return keyValue;
};

/** Returns the current subdomain based on location API */
export const getCurrentSubdomain = () =>
    window.__PRERENDER_INJECTED && window.__PRERENDER_INJECTED.subdomain
        ? window.__PRERENDER_INJECTED.subdomain
        : window.location.hostname.split(".")[0];

/**
 * Returns true if the given categoryLabel and routeName belong to the same
 * subdomain
 * @param {String} routeName - A $route.name string
 * @param {String} subdomain - A subdomain/filter value (from the SearchBox
 * dropdown)
 * @return {Boolean} true if routeName and categoryLabel belong to the same
 * subdomain
 */
export const sameSubdomain = (routeName, subdomain) => {
    return routeName.toLowerCase().indexOf(subdomain.toLowerCase()) >= 0;
};

/**
 * Legacy YDR Ads use a Title Cased subdomain string as part of their ID, so
 * this function gets a subdomain string and returns the proper format for Ads
 * @param {String} subdomain - A valid subdomain string or undefined to use
 * current subdomain.
 * @return {String} A valid subdomain with proper case to be used as part of an
 * advertisement ID.
 */
export const adSubdomain = subdomain => {
    subdomain = subdomain || getCurrentSubdomain();
    const isValidSubdomain = Object.values(subdomains).includes(subdomain);
    subdomain = isValidSubdomain ? subdomain : subdomains.WWW;
    return subdomain[0].toUpperCase() + subdomain.substring(1).toLowerCase();
};

/**
 * Replaces the current location to navigate to the given path. This function is
 * used to force a programatic navigation and Vue Router cannot be used because
 * the navigation leads to another subdomain.
 * @param {String} subdomain - A valid YDR subdomain. See common/consts.js
 * @param {String} path - A relative URL from subdomains root. MUST have a
 * leading "/"
 */
export const navigateTo = (subdomain, path) => {
    const url = getSubdomainRootURL(subdomain) + path;
    window.location.assign(url);
};

const getSubdomainRootURL = subdomain => {
    subdomain = Object.values(subdomains).includes(subdomain) ? subdomain : subdomains.WWW;
    const key = Object.keys(subdomains).find(key => subdomains[key] === subdomain);
    return root[key] || root.WWW;
};

/**
 * Does a POST request to YDR v1 /search endpoint (this will perform the search
 * and redirect the user to the results page.
 * @param {String} subdomain - A valid YDR subdomain. See common/consts.js
 * @param {String} term - A search term valid
 */
export const legacyPostSearch = (subdomain, term) => {
    const url = `https://${subdomain}.yourdictionary.com/search/`;
    let f = document.createElement("form");
    f.setAttribute("method", "POST");
    f.setAttribute("action", url);

    let q = document.createElement("input");
    q.setAttribute("type", "hidden");
    q.setAttribute("name", "ydQ");
    q.setAttribute("value", term);

    f.appendChild(q);
    document.body.appendChild(f);
    f.submit();
};

/**
 * Return true if window's width is less than the threshold
 * @return {Boolean} true if window's width is of a small device
 */
export const isSmallDevice = () => {
    return window.innerWidth < SMALL_DEVICE_WIDTH;
};

/**
 * Return true if window's width is less than the threshold
 * @return {Boolean} true if window's width is of a small device
 */
export const isMediumDevice = () => {
    return window.innerWidth < MEDIUM_DEVICE_WIDTH;
};

/**
 * Return capitalized string
 * @param {String}
 */
export const capitalize = str => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Get url path replace "/" to "." to get article's data
 * e.g "/article/name/of/article" -> ".article.name.of.article"
 * @param {String} path - url path
 * @return {String} without ".html" in the path
 */
export const getArticleSlug = path => {
    return path.endsWith(".html")
        ? path.slice(0, -5).replace(/\//g, ".")
        : path.replace(/\//g, ".");
};

/**
 * A wrapper function to track custom events in GA. For more
 * info on what these params mean, please consult the official
 * Google documentation:
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/events
 * @param {object} ga The this.$ga object injected by the Vue component
 * @param {object} eventData An object containing the category, action, label, value, and AB exp data. Only the first two are mandatory.
 */
export const trackEvent = (ga, eventData) => {
    if (typeof window.__PRERENDER_INJECTED !== "undefined") return false;
    if (typeof ga !== "object" || typeof ga.event !== "function") return false;

    // The GA tracking requires at least the category and the action of the event.
    if (!eventData.eventCategory || !eventData.eventAction) return false;

    eventData.eventLabel = eventData.eventLabel || "";
    eventData.eventValue = eventData.eventValue || null;

    ga.event(eventData);
};

/**
 * Write the CCPA Privacy string into a cookie to be read by the API.
 * @param {string} value The value of the cookie to be written. Defaults to 1--- as per understood specs.
 */
export function writeCCPACookie(value = "1---") {
    let d = new Date();
    d.setTime(d.getTime() + 10 * 365 * 24 * 60 * 60 * 1000); // Make it roughly 10 years from now.
    const expires = "expires=" + d.toUTCString();
    document.cookie = `usprivacy=${value};${expires};path=/;Domain=.yourdictionary.com;SameSite=Lax;secure`;
}
/**
 * Read the CCPA Privacy string from the 'usprivacy' cookie.
 */
export function readCCPACookie() {
    var cookieValue = "";
    var cookieName = "usprivacy=";
    var cookieArray = document.cookie.split(";");
    for (var i = 0; i < cookieArray.length; i++) {
        var cookie = cookieArray[i];
        while (cookie.charAt(0) == " ") {
            cookie = cookie.substring(1);
        }
        if (cookie.indexOf(cookieName) == 0) {
            cookieValue = cookie.substring(cookieName.length, cookie.length);
            break;
        }
    }

    // If the cookie cannot be found or has an invalid value, default it back to 1---, the CCPA default privacy string value.
    if (!/^[1][nNyY-][nNyY-][nNyY-]$/.test(cookieValue)) {
        cookieValue = "1---";
    }

    return cookieValue;
}



// WEBPACK FOOTER //
// ./src/common/functions.js